import {
  Anchor,
  Box,
  Flex,
  ScrollArea,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { MakeCard } from "../../elements/MakeCard";
import RecentCarsEmptyState from "./RecentCarsEmptyState";

import { TextSerif } from "@/components/elements";
import { paths } from "@/config/paths";
import { useRecentCars } from "@/hooks/useRecentCars";
import { defaultCurrency } from "@/lib/currency";
import { formatCurrency } from "@/utils/formatEuro";

import type { Currency } from "@/types/currency";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type RecentCarsProps = MantineStyleProps;

export const RecentCars: FC<RecentCarsProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const height = "21em";
  const fontSize = { base: "xs", sm: "sm" };
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "#a7a7a7A" : "secondary.4";

  const { recentlyViewedCars } = useRecentCars();

  if (!recentlyViewedCars || recentlyViewedCars.length === 0) {
    return (
      <>
        <Title order={4} opacity={0.3}>
          {t("market.last_viewed")}
        </Title>
        <Flex align="center" justify="center" w="100%" h="100%">
          <Box w="100%" h={height}>
            <RecentCarsEmptyState />
          </Box>
        </Flex>
      </>
    );
  }

  return (
    <>
      <Title order={4}>{t("market.last_viewed")}</Title>
      <Stack gap="xs" {...props}>
        <Flex w="100%" justify="end">
          <TextSerif size="xs" c={textColor}>
            {t("market.table.cols.current_valuation")}
          </TextSerif>
        </Flex>

        <ScrollArea
          h={height}
          type="scroll"
          scrollbarSize={4}
          styles={{
            viewport: { paddingRight: recentlyViewedCars.length <= 6 ? 0 : 8 },
          }}
        >
          {recentlyViewedCars.map((car) => (
            <Flex key={`RecentCars_KEY_${car.id}`} align="center" justify="space-between" h={42}>
              <Flex align="center" gap="lg" style={{ minWidth: 0, flex: 1 }}>
                <Box w={32}>
                  <MakeCard make={car.make_name} />
                </Box>
                <Anchor
                  component={Link}
                  to={paths.app.markets.car.getHref(car.id)}
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Text size="sm" fz={fontSize} truncate="end">
                    {car.make_name} {car.model_name}
                  </Text>
                </Anchor>
              </Flex>

              <Box ta="right" miw={80} style={{ flexShrink: 0 }}>
                <Text fz={fontSize}>{formatCurrency(car.current_valuation, currency.code)}</Text>
              </Box>
            </Flex>
          ))}
        </ScrollArea>
      </Stack>
    </>
  );
};
